/**
 * Document symbols (v0.2) — outline view for a single file. Walks the
 * file's parsed declarations and emits a hierarchical symbol tree.
 */

import {
  SymbolKind as LspSymbolKind,
  type DocumentSymbol,
} from "vscode-languageserver";
import type {
  Body,
  ExtensionDecl,
  SurfaceDecl,
  InterfaceDecl,
  ModuleDecl,
  ProcessDecl,
  SubprocessDecl,
  TopLevelDecl,
  TypeDecl,
  ViewDeclV2,
} from "@archlang/engine";
import type { Workspace } from "../workspace/workspace.js";
import { toLspRange } from "../lsp-types.js";

export function provideDocumentSymbols(
  workspace: Workspace,
  uri: string
): DocumentSymbol[] {
  // Markdown document (§4.15): outline its section headings.
  const doc = workspace.getDocument(uri);
  if (doc) return documentSectionSymbols(workspace, uri);

  const file = workspace.getFile(uri);
  if (!file) return [];

  const out: DocumentSymbol[] = [];
  for (const top of file.file.declarations) {
    const sym = topLevelToSymbol(top);
    if (sym) out.push(sym);
  }
  return out;
}

/**
 * Outline a `.md` document's section headings as a hierarchical symbol tree,
 * nesting deeper headings under shallower ones (§4.15).
 */
function documentSectionSymbols(
  workspace: Workspace,
  uri: string
): DocumentSymbol[] {
  const doc = workspace.getDocument(uri);
  const text = workspace.getDocumentText(uri);
  if (!doc || text === undefined) return [];

  const out: DocumentSymbol[] = [];
  const stack: { level: number; sym: DocumentSymbol }[] = [];
  const sections = doc.sections;
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i]!;
    // The section spans from its heading down to the next heading of equal or
    // shallower depth (else end-of-file).
    let endOffset = text.length;
    for (let j = i + 1; j < sections.length; j++) {
      if (sections[j]!.level <= s.level) {
        endOffset = sections[j]!.offset;
        break;
      }
    }
    const start = positionAt(text, s.offset);
    const sym: DocumentSymbol = {
      name: s.text,
      detail: `h${s.level}`,
      kind: LspSymbolKind.String,
      range: { start, end: positionAt(text, endOffset) },
      selectionRange: { start, end: positionAt(text, lineEndOffset(text, s.offset)) },
      children: [],
    };
    while (stack.length > 0 && stack[stack.length - 1]!.level >= s.level) stack.pop();
    if (stack.length === 0) out.push(sym);
    else stack[stack.length - 1]!.sym.children!.push(sym);
    stack.push({ level: s.level, sym });
  }
  return out;
}

function positionAt(
  text: string,
  offset: number
): { line: number; character: number } {
  let line = 0;
  let character = 0;
  const limit = Math.min(offset, text.length);
  for (let i = 0; i < limit; i++) {
    if (text[i] === "\n") {
      line++;
      character = 0;
    } else {
      character++;
    }
  }
  return { line, character };
}

/** Offset of the end of the line containing `offset` (exclusive of `\n`). */
function lineEndOffset(text: string, offset: number): number {
  const nl = text.indexOf("\n", offset);
  return nl === -1 ? text.length : nl;
}

function topLevelToSymbol(top: TopLevelDecl): DocumentSymbol | null {
  switch (top.kind) {
    case "module":
      return moduleToSymbol(top.decl);
    case "type":
      return typeToSymbol(top.decl);
    case "process":
      return processToSymbol(top.decl);
    case "subprocess":
      return subprocessToSymbol(top.decl);
    case "view":
      return viewToSymbol(top.decl);
    case "extension":
      return extensionToSymbol(top.decl);
    default:
      // Top-level kinds with no outline representation yet (v0.10 `query` /
      // `policy` governance decls, inert until their evaluator lands). Robust to
      // the union growing — a new kind falls through here, not to a type error.
      return null;
  }
}

/** Body members (surfaces / interfaces / sub-modules) as outline children. */
function bodyChildren(body: Body): DocumentSymbol[] {
  const children: DocumentSymbol[] = [];
  for (const f of body.surfaces) {
    const s = surfaceToSymbol(f);
    if (s) children.push(s);
  }
  for (const i of body.interfaces) {
    const s = interfaceToSymbol(i);
    if (s) children.push(s);
  }
  for (const sub of body.modules) {
    const s = moduleToSymbol(sub);
    if (s) children.push(s);
  }
  return children;
}

function moduleToSymbol(m: ModuleDecl): DocumentSymbol | null {
  if (!m.source || !m.nameSource) return null;
  return {
    name: m.name,
    detail: m.typeName,
    kind: LspSymbolKind.Class,
    range: toLspRange(m.source),
    selectionRange: toLspRange(m.nameSource),
    children: bodyChildren(m.body),
  };
}

/** v0.8.2 §4.16: an `in Target { … }` extension block shows its members in the
 *  outline, grouped under the target it reopens. */
function extensionToSymbol(e: ExtensionDecl): DocumentSymbol | null {
  if (!e.source || !e.targetSource) return null;
  return {
    name: `in ${e.target.join(".")}`,
    kind: LspSymbolKind.Namespace,
    range: toLspRange(e.source),
    selectionRange: toLspRange(e.targetSource),
    children: bodyChildren(e.body),
  };
}

function surfaceToSymbol(f: SurfaceDecl): DocumentSymbol | null {
  if (!f.source || !f.nameSource) return null;
  const children: DocumentSymbol[] = [];
  for (const sub of f.body.surfaces) {
    const s = surfaceToSymbol(sub);
    if (s) children.push(s);
  }
  for (const i of f.body.interfaces) {
    const s = interfaceToSymbol(i);
    if (s) children.push(s);
  }
  return {
    name: f.name,
    detail: f.typeName,
    kind: LspSymbolKind.Namespace,
    range: toLspRange(f.source),
    selectionRange: toLspRange(f.nameSource),
    children,
  };
}

function interfaceToSymbol(i: InterfaceDecl): DocumentSymbol | null {
  if (!i.source || !i.nameSource) return null;
  return {
    name: i.name,
    detail: i.typeName,
    kind: LspSymbolKind.Method,
    range: toLspRange(i.source),
    selectionRange: toLspRange(i.nameSource),
  };
}

function typeToSymbol(t: TypeDecl): DocumentSymbol | null {
  if (!t.source || !t.nameSource) return null;
  return {
    name: t.name,
    detail: `type ${t.parentType}`,
    kind: LspSymbolKind.Interface,
    range: toLspRange(t.source),
    selectionRange: toLspRange(t.nameSource),
  };
}

function processToSymbol(p: ProcessDecl): DocumentSymbol | null {
  if (!p.source || !p.nameSource) return null;
  return {
    name: p.name,
    kind: LspSymbolKind.Function,
    range: toLspRange(p.source),
    selectionRange: toLspRange(p.nameSource),
  };
}

function subprocessToSymbol(s: SubprocessDecl): DocumentSymbol | null {
  if (!s.source || !s.nameSource) return null;
  return {
    name: s.name,
    detail: s.params.length > 0 ? `(${s.params.join(", ")})` : undefined,
    kind: LspSymbolKind.Function,
    range: toLspRange(s.source),
    selectionRange: toLspRange(s.nameSource),
  };
}

function viewToSymbol(v: ViewDeclV2): DocumentSymbol | null {
  if (!v.source || !v.nameSource) return null;
  return {
    name: v.name,
    kind: LspSymbolKind.Object,
    range: toLspRange(v.source),
    selectionRange: toLspRange(v.nameSource),
  };
}
